import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Navbar, Footer } from "../components/Layout";
import { useNoIndex } from "../hooks/useNoIndex";
import { Brain, Flame, Trophy, Volume2, Sparkles, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useTTS } from "../hooks/useTTS";

const RATINGS = [
  { value: 1, label: "Encore", color: "#E94A3F", hint: "<1 min" },
  { value: 2, label: "Difficile", color: "#FFD166", hint: "10 min" },
  { value: 3, label: "Bien", color: "#8CA07A", hint: "1-6 jours" },
  { value: 4, label: "Facile", color: "#1A2A40", hint: "+1 semaine" },
];

const Review = () => {
  useNoIndex();
  const { user, loading: authLoading } = useAuth();
  const [cards, setCards] = useState([]);
  const [idx, setIdx] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  const [stats, setStats] = useState({ total: 0, due: 0, learned: 0 });
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);

  const loadDue = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/srs/due?limit=20");
      setCards(data.cards || []);
      setIdx(0);
      setShowAnswer(false);
      const st = await api.get("/srs/stats");
      setStats(st.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (user) loadDue(); }, [user]);

  const { speak: ttsSpeak } = useTTS();
  const speak = (t) => ttsSpeak(t);

  const rate = async (rating) => {
    const card = cards[idx];
    try {
      await api.post("/srs/review", { card_id: card.card_id, rating });
      if (idx + 1 < cards.length) {
        setIdx(idx + 1);
        setShowAnswer(false);
      } else {
        toast.success("Session terminée ! 🎉");
        loadDue();
      }
    } catch {
      toast.error("Erreur");
    }
  };

  const seed = async (level, count = 20) => {
    setSeeding(true);
    try {
      const { data } = await api.post("/srs/seed", { level, count });
      if (data.added === 0) {
        toast.info("Aucune nouvelle carte (déjà toutes ajoutées pour ce niveau)");
      } else {
        toast.success(`+${data.added} cartes ${level} ajoutées`);
      }
      await loadDue();
    } catch (err) {
      if (err.response?.status === 402) toast.error("Premium requis pour les niveaux N4 et N3");
      else toast.error("Erreur");
    } finally {
      setSeeding(false);
    }
  };

  if (authLoading) return <div className="washi-bg min-h-screen"><Navbar /></div>;
  if (!user) return <Navigate to="/login" replace />;

  const card = cards[idx];

  return (
    <div className="washi-bg min-h-screen" data-testid="review-page">
      <Navbar />
      <div className="max-w-3xl mx-auto px-6 sm:px-12 py-12">
        <div className="flex items-end justify-between flex-wrap gap-3 mb-8">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#E94A3F] mb-2">Révisions · 復習</div>
            <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter leading-[1] text-[#1A2A40]">
              Ta session du jour.
            </h1>
          </div>
          <div className="flex gap-2">
            <StatPill icon={<Flame />} k={stats.due} v="à réviser" c="#E94A3F" />
            <StatPill icon={<Brain />} k={stats.learned} v="mémorisés" c="#8CA07A" />
            <StatPill icon={<Trophy />} k={stats.total} v="total" c="#FFD166" />
          </div>
        </div>

        {loading ? (
          <p className="text-center text-[#4A5568] py-20">Chargement...</p>
        ) : cards.length === 0 ? (
          <div className="bg-[#FDFBF7] rounded-3xl p-10 border-2 border-[#1A2A40]/10 text-center" data-testid="no-cards">
            <Sparkles className="w-12 h-12 text-[#FFD166] mx-auto mb-4" />
            <h2 className="font-display font-black text-2xl mb-3 text-[#1A2A40]">
              {stats.total === 0 ? "Pas encore de cartes" : "Aucune révision pour l'instant, bravo !"}
            </h2>
            <p className="text-[#4A5568] mb-6">
              {stats.total === 0
                ? "Ajoutez votre premier paquet pour démarrer la mémorisation espacée."
                : "Reviens plus tard pour la prochaine session."}
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              <button onClick={() => seed("N5", 20)} disabled={seeding} data-testid="seed-n5-btn" className="bg-[#E94A3F] text-white font-bold py-3 px-5 rounded-xl shadow-brut-sm hover-lift inline-flex items-center gap-2 disabled:opacity-60">
                + 20 cartes N5
              </button>
              <button onClick={() => seed("N4", 20)} disabled={seeding} data-testid="seed-n4-btn" className="bg-[#1A2A40] text-white font-bold py-3 px-5 rounded-xl shadow-brut-sm hover-lift inline-flex items-center gap-2 disabled:opacity-60">
                + 20 cartes N4 <span className="text-[10px] bg-[#FFD166] text-[#1A2A40] px-1.5 py-0.5 rounded">PRO</span>
              </button>
              <button onClick={() => seed("N3", 20)} disabled={seeding} data-testid="seed-n3-btn" className="bg-[#1A2A40] text-white font-bold py-3 px-5 rounded-xl shadow-brut-sm hover-lift inline-flex items-center gap-2 disabled:opacity-60">
                + 20 cartes N3 <span className="text-[10px] bg-[#FFD166] text-[#1A2A40] px-1.5 py-0.5 rounded">PRO</span>
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="text-center text-xs text-[#A0AEC0] mb-4">
              Carte {idx + 1} sur {cards.length}
            </div>
            <div
              onClick={() => !showAnswer && setShowAnswer(true)}
              data-testid="srs-card"
              className={`bg-[#FDFBF7] rounded-3xl p-10 sm:p-14 border-2 border-[#1A2A40] shadow-brut min-h-[300px] flex flex-col items-center justify-center text-center ${!showAnswer ? "cursor-pointer hover-lift" : ""}`}
            >
              <div className="font-jp font-bold text-6xl sm:text-7xl text-[#1A2A40] mb-4" data-testid="card-front">
                {card.payload.jp}
              </div>
              {showAnswer && (
                <div className="animate-fade-up" data-testid="card-back">
                  <div className="text-xl text-[#E94A3F] font-mono mb-2">{card.payload.romaji}</div>
                  <div className="text-2xl text-[#1A2A40] font-bold">{card.payload.fr}</div>
                  {card.payload.category && (
                    <div className="text-xs uppercase tracking-widest text-[#A0AEC0] mt-3">{card.payload.category}</div>
                  )}
                  <button onClick={(e) => { e.stopPropagation(); speak(card.payload.jp); }} data-testid="speak-card-btn" className="mt-5 inline-flex items-center gap-2 bg-[#F3F6F1] hover:bg-[#FFD166] rounded-full px-4 py-2 text-sm font-medium">
                    <Volume2 className="w-4 h-4" /> Écouter
                  </button>
                </div>
              )}
              {!showAnswer && (
                <div className="text-sm text-[#A0AEC0] mt-6">Cliquez pour révéler</div>
              )}
            </div>

            {showAnswer && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6" data-testid="rating-buttons">
                {RATINGS.map((r) => (
                  <button
                    key={r.value}
                    onClick={() => rate(r.value)}
                    data-testid={`rate-${r.value}`}
                    className="font-bold py-4 rounded-xl shadow-brut-sm hover-lift text-white text-sm"
                    style={{ background: r.color }}
                  >
                    <div>{r.label}</div>
                    <div className="text-[10px] opacity-80 font-normal">{r.hint}</div>
                  </button>
                ))}
              </div>
            )}

            <p className="text-center text-xs text-[#4A5568] mt-6">
              💡 Algorithme SM-2 : plus tu mémorises facilement, plus la prochaine révision sera espacée.
            </p>
          </>
        )}
      </div>
      <Footer />
    </div>
  );
};

const StatPill = ({ icon, k, v, c }) => (
  <div className="bg-[#FDFBF7] border-2 border-[#1A2A40]/10 rounded-2xl px-4 py-2 flex items-center gap-2">
    <div style={{ color: c }}>{icon}</div>
    <div>
      <div className="font-display font-black text-lg leading-none">{k}</div>
      <div className="text-[10px] uppercase tracking-wider text-[#4A5568]">{v}</div>
    </div>
  </div>
);

export default Review;
